// Blog posts: interview prep, DSA and engineering deep dives. Same block
// format as blogPosts.js (rendered by components/ContentBlocks).

export const techPosts = [
  {
    slug: 'ace-the-machine-coding-round',
    title: 'How to Ace the Machine Coding Round',
    excerpt: 'What interviewers grade in a 90-minute build, a minute-by-minute game plan, and the code structure that earns a “strong hire”.',
    category: 'Interview Tips',
    tags: ['Machine Coding', 'React', 'Interviews'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'In a machine coding round you get a problem like “build a kanban board” or “build an autocomplete” and 60–120 minutes to produce working code. It is the closest an interview gets to real work, and it rewards a very different approach from DSA rounds.' },

      { type: 'h2', text: 'What interviewers actually grade' },
      { type: 'table', headers: ['Area', 'What they look for'], rows: [
        ['Working software', 'The core flow works end to end when they run it'],
        ['Structure', 'Clear components or modules, sensible state, no giant file'],
        ['Code quality', 'Readable names, small functions, no copy-paste blocks'],
        ['Edge cases', 'Empty states, errors, loading, invalid input'],
        ['Extensibility', 'Adding the next feature would not require a rewrite'],
        ['Communication', 'You clarified requirements and explained trade-offs']
      ] },
      { type: 'callout', variant: 'tip', title: 'The golden rule', text: 'A complete, simple solution beats an impressive, unfinished one. Get the core working first, then improve.' },

      { type: 'h2', text: 'A minute-by-minute plan for a 90-minute round' },
      { type: 'ol', items: [
        '**0–10 min: clarify.** Ask about must-have vs nice-to-have features, data source (mock or API), persistence, and whether styling matters.',
        '**10–20 min: design.** Sketch components, state shape and data flow. Say it out loud.',
        '**20–60 min: build the core.** The main user flow, working end to end, even if ugly.',
        '**60–75 min: edge cases.** Empty, loading and error states, input validation, keyboard support.',
        '**75–85 min: clean up.** Extract helpers, rename things, remove dead code.',
        '**85–90 min: walk through.** Demo it, explain decisions and what you would add next.'
      ] },

      { type: 'h2', text: 'Design your state before you write JSX' },
      { type: 'p', text: 'Most messy solutions come from unclear state. For a kanban board, decide the shape first:' },
      { type: 'code', language: 'javascript', code: `const initialBoard = {
  columns: [
    { id: "todo", title: "To do", taskIds: ["t1", "t2"] },
    { id: "doing", title: "In progress", taskIds: [] },
    { id: "done", title: "Done", taskIds: ["t3"] }
  ],
  tasks: {
    t1: { id: "t1", title: "Design schema" },
    t2: { id: "t2", title: "Build API" },
    t3: { id: "t3", title: "Set up repo" }
  }
};

function moveTask(board, taskId, toColumnId) {
  return {
    ...board,
    columns: board.columns.map((col) => {
      const without = col.taskIds.filter((id) => id !== taskId);
      return col.id === toColumnId ? { ...col, taskIds: [...without, taskId] } : { ...col, taskIds: without };
    })
  };
}

const next = moveTask(initialBoard, "t2", "doing");
console.log(next.columns.map((c) => \`\${c.id}: \${c.taskIds.join(",") || "-"}\`).join(" | "));`, output: `todo: t1 | doing: t2 | done: t3` },
      { type: 'p', text: 'Storing tasks by id and columns as lists of ids makes moving, editing and deleting simple and avoids duplicated data. Pure functions like `moveTask` are easy to test and easy to explain.' },

      { type: 'h2', text: 'Structure that scales' },
      { type: 'ul', items: [
        'Separate logic from UI: a `useBoard` hook or reducer holds the rules; components render.',
        'One component per concept: `Board`, `Column`, `TaskCard`, `AddTaskForm`.',
        'Keep data access behind a small function (`api.getTasks()`) so swapping mock data for a real API is trivial.',
        'Name things after the domain, not the UI: `moveTask`, not `handleDrop2`.'
      ] },

      { type: 'h2', text: 'Edge cases that impress' },
      { type: 'ul', items: [
        'Empty states with a helpful message (“No tasks yet. Add one.”).',
        'Loading and error states for any async data.',
        'Trimming and validating input; preventing duplicate submissions.',
        'Keyboard accessibility: buttons are buttons, forms submit on Enter, focus is visible.',
        'Persisting to `localStorage` if refreshing would lose work, with a try/catch around it.'
      ] },

      { type: 'h2', text: 'How to talk while you code' },
      { type: 'ul', items: [
        'Narrate decisions, not keystrokes: “I’ll keep tasks in a map so moves are O(1) lookups.”',
        'Flag shortcuts honestly: “I’m hard-coding the columns for now; they could come from the API.”',
        'If you are running out of time, say what you would do next and why.'
      ] },

      { type: 'h2', text: 'Common ways candidates lose the round' },
      { type: 'ul', items: [
        'Spending 30 minutes on styling or setup before anything works.',
        'One 400-line component with all the logic inside.',
        'Mutating state directly and fighting re-render bugs.',
        'Silent coding with no explanation of trade-offs.',
        'Not running the code until the last five minutes.'
      ] },

      { type: 'h2', text: 'Practise these classics' },
      { type: 'p', text: 'Autocomplete with debouncing, modal dialog, infinite scroll feed, kanban board, star rating, file explorer tree, image carousel, todo app with filters, OTP input, and a simple spreadsheet. Build each in 90 minutes with a timer, then compare your solution with one written a week later.' },

      { type: 'h2', text: 'Worked example: autocomplete in 90 minutes' },
      { type: 'p', text: 'Autocomplete is one of the most frequently asked problems because it touches state, async data, performance and accessibility. Here is the core you should be able to write from memory.' },
      { type: 'code', language: 'jsx', code: `function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function Autocomplete({ search }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(-1);
  const debounced = useDebounced(query.trim());

  useEffect(() => {
    if (debounced.length < 2) { setResults([]); return; }
    let cancelled = false;                       // ignore stale responses
    search(debounced).then((items) => { if (!cancelled) setResults(items); });
    return () => { cancelled = true; };
  }, [debounced, search]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") setActive((i) => Math.min(i + 1, results.length - 1));
    if (e.key === "ArrowUp") setActive((i) => Math.max(i - 1, 0));
    if (e.key === "Enter" && results[active]) setQuery(results[active].label);
  };

  return (
    <div>
      <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
             role="combobox" aria-expanded={results.length > 0} aria-controls="ac-list" />
      <ul id="ac-list" role="listbox">
        {results.map((r, i) => (
          <li key={r.id} role="option" aria-selected={i === active} onClick={() => setQuery(r.label)}>
            {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
}` },
      { type: 'p', text: 'Talking points that earn credit: debouncing reduces requests, the `cancelled` flag prevents an older response overwriting a newer one, keyboard navigation and ARIA roles make it accessible, and `search` is passed in so it can be mocked in tests.' },

      { type: 'h2', text: 'Checklist before you say “done”' },
      { type: 'ul', items: [
        'Run through the main flow once more, out loud, in front of the interviewer.',
        'Try the obvious edge cases: empty input, very long text, rapid clicks, a failing request.',
        'Remove `console.log`s and commented-out code.',
        'Make sure names describe what things are, and no function is doing three jobs.',
        'Prepare a one-minute summary: structure, key decisions, what you would add next.'
      ] },

      { type: 'h2', text: 'Frequently asked questions' },
      { type: 'qa', question: 'Can I use libraries?', answer: ['Ask. Many companies allow common utilities but want core logic (state, data flow, the main interaction) written by you. Using a library for drag and drop may be fine; using a ready-made autocomplete component is not.'] },
      { type: 'qa', question: 'Does styling matter?', answer: ['Usually only a little. Clean, readable layout is enough unless the role is design-heavy. Spend time on behaviour and structure first.'] },
      { type: 'qa', question: 'Should I write tests?', answer: ['If time allows, a couple of tests for your core logic (like `moveTask`) are a strong signal. Mention what else you would test if you run out of time.'] },

      { type: 'summary', items: [
        'Clarify, design, build the core, handle edge cases, clean up, then demo.',
        'Design state first; pure functions keep logic simple and testable.',
        'Separate logic from UI and name things after the domain.',
        'Working and simple beats ambitious and broken.'
      ] }
    ]
  },
  {
    slug: 'behavioural-interviews-for-engineers',
    title: 'Behavioural Interviews for Engineers: STAR Stories That Actually Work',
    excerpt: 'Why good engineers fail behavioural rounds, how to build a story bank, and example answers for the questions you will definitely get.',
    category: 'Interview Tips',
    tags: ['Behavioural', 'STAR', 'Interviews'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'Many engineers prepare for weeks for coding rounds and walk into the behavioural round with nothing ready. Then they ramble, forget their best examples, or say “we” so often the interviewer cannot tell what they did. Behavioural rounds can decide your level and even the offer itself, so they deserve real preparation.' },

      { type: 'h2', text: 'What the interviewer is really checking' },
      { type: 'ul', items: [
        '**Ownership**: do you take responsibility beyond your assigned tasks?',
        '**Collaboration**: can you disagree without damaging relationships?',
        '**Learning**: do you grow from mistakes and feedback?',
        '**Impact**: does your work change outcomes that matter?',
        '**Self-awareness**: can you describe your own strengths and gaps honestly?'
      ] },

      { type: 'h2', text: 'The STAR structure' },
      { type: 'table', headers: ['Part', 'Share of the answer', 'Purpose'], rows: [
        ['Situation', '~10%', 'Just enough context'],
        ['Task', '~10%', 'Your responsibility and what was at stake'],
        ['Action', '~60%', 'What you did, and why. Say “I”'],
        ['Result', '~20%', 'Measurable outcome and what you learned']
      ] },
      { type: 'tip', text: 'Aim for about two minutes per story. Most first attempts spend too long on the situation and too little on the actions.' },

      { type: 'h2', text: 'Build a story bank' },
      { type: 'p', text: 'Prepare 6–8 real stories that can each answer several questions. For every story note the challenge, your actions, the result with numbers, and one lesson.' },
      { type: 'table', headers: ['Story type', 'Answers questions about'], rows: [
        ['A hard technical problem you solved', 'Challenges, technical depth, persistence'],
        ['A mistake or failure', 'Failure, learning, accountability'],
        ['A disagreement with a teammate or lead', 'Conflict, influence, collaboration'],
        ['Something you did beyond your role', 'Ownership, initiative, leadership'],
        ['A tight deadline or changing priorities', 'Prioritisation, pressure, communication'],
        ['Helping someone else grow', 'Mentoring, teamwork, leadership']
      ] },

      { type: 'h2', text: 'Example: “Tell me about a time you failed”' },
      { type: 'qa', question: 'A strong answer', answer: [
        '**Situation**: “I shipped a database migration that renamed a column used by our reporting job.”',
        '**Task**: “I owned the migration and its rollout.”',
        '**Action**: “I had tested the API but not other consumers of that table. When the finance report failed the next morning, I told my lead immediately, switched the report to a view exposing the old column within an hour, and reran it. Then I wrote a short post-mortem and added a checklist step: search every repository and scheduled job for a table before changing it. I also proposed adding new columns first and removing old ones later.”',
        '**Result**: “The report was delayed two hours. We have run many migrations since without breaking a consumer, and I now think about who depends on a change before I make it.”'
      ] },
      { type: 'p', text: 'Notice what makes it work: a real mistake, immediate ownership, a systemic fix, and a lesson that changed behaviour.' },

      { type: 'h2', text: 'Example: “Tell me about a conflict”' },
      { type: 'qa', question: 'A strong answer', answer: [
        '“A senior teammate wanted to run new analytics queries on our main database; I was worried they would slow the app. Instead of arguing in comments, I asked for a short call and first asked about his concerns. He was worried about the cost of adding new infrastructure, which was fair. We agreed to test: I ran the heaviest queries on a production-sized copy, and they spiked CPU badly. We settled on a read replica, which added no new technology. We shipped on time, the app’s latency was unaffected, and he started asking me to review his designs early.”'
      ] },

      { type: 'h2', text: 'Questions you will almost certainly get' },
      { type: 'ul', items: [
        'Tell me about yourself.',
        'Your most challenging project.',
        'A time you failed or made a mistake.',
        'A conflict with a teammate or manager.',
        'A time you went beyond your role.',
        'How you handle tight deadlines.',
        'Critical feedback you received.',
        'Why do you want to join us? Why are you leaving?'
      ] },

      { type: 'h2', text: 'Mistakes that cost offers' },
      { type: 'ul', items: [
        'Fake weaknesses (“I’m a perfectionist”) and fake failures.',
        'Blaming teammates, managers or unclear requirements.',
        'Saying “we” throughout so your contribution is invisible.',
        'No numbers or outcomes at all.',
        'Memorised answers that collapse under follow-up questions.'
      ] },

      { type: 'h2', text: 'Ask good questions at the end' },
      { type: 'p', text: 'Always ask two or three: “What would success look like in the first six months?”, “What is the team’s biggest technical challenge right now?”, “How are technical decisions made?”. Good questions signal genuine interest and help you decide if you want the job.' },

      { type: 'h2', text: '“Tell me about yourself”: a structure that works' },
      { type: 'p', text: 'This is usually the first question, and it sets the tone. Use present, past, future in about 90 seconds:' },
      { type: 'ol', items: [
        '**Present**: your current role or focus and your strongest skills.',
        '**Past**: one or two achievements that prove those skills, with a number if possible.',
        '**Future**: why this role is the logical next step for you.'
      ] },
      { type: 'qa', question: 'Example answer', answer: [
        '“I’m a backend engineer working mostly with Java and Spring Boot. For the last two years I’ve owned our payments service: I redesigned the retry logic, which cut failed payment complaints significantly, and I lead our on-call rotation for that service. Before that I worked on internal tools, which taught me to talk to users before building. I’m now looking for a product team operating at larger scale, which is why this role, with its focus on reliability for millions of users, appeals to me.”'
      ] },

      { type: 'h2', text: '“What is your biggest weakness?”' },
      { type: 'p', text: 'Pick something real, relevant but not central to the role, and show what you are doing about it. For example: hesitating to speak up in design discussions, and the habit you built of writing concerns down and raising them as questions. Avoid fake weaknesses and anything that disqualifies you for the job.' },

      { type: 'h2', text: 'Adjusting for your level' },
      { type: 'table', headers: ['Level', 'What your stories should show'], rows: [
        ['Fresher / intern', 'Learning quickly, taking feedback well, finishing what you start (college, projects and internships count)'],
        ['1–3 years', 'Owning features end to end, debugging production issues, collaborating across teams'],
        ['Senior', 'Influencing decisions, mentoring, handling ambiguity, improving how the team works'],
        ['Lead', 'Setting direction, resolving conflict between people and teams, delivering through others']
      ] },

      { type: 'h2', text: 'Practise the right way' },
      { type: 'ul', items: [
        'Say your stories out loud and time them. Writing them is not the same as telling them.',
        'Record yourself once; you will notice filler words and missing results immediately.',
        'Ask a friend to interrupt with follow-ups: “why did you choose that?”, “what would you do differently?”.',
        'Keep notes with bullet points, not scripts, so you sound natural.'
      ] },

      { type: 'summary', items: [
        'Behavioural rounds test ownership, collaboration, learning and impact.',
        'Use STAR and spend most of the time on your actions.',
        'Prepare 6–8 real stories that each answer several questions.',
        'Be honest, specific and accountable; real stories survive follow-ups.'
      ] }
    ]
  },
  {
    slug: 'design-a-url-shortener',
    title: 'System Design Walkthrough: Design a URL Shortener',
    excerpt: 'The classic interview question, solved step by step: requirements, estimates, API, ID generation, caching, analytics and trade-offs.',
    category: 'System Design',
    tags: ['System Design', 'Caching', 'Scalability'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: '“Design a URL shortener like bit.ly” is one of the most common system design interview questions, because it is small enough to finish in 45 minutes but touches everything that matters: ID generation, storage, caching, scale and trade-offs. Here is how to walk through it the way strong candidates do.' },

      { type: 'h2', text: '1. Clarify requirements' },
      { type: 'p', text: 'Never jump into boxes and arrows. Agree on scope first.' },
      { type: 'ul', items: [
        '**Functional**: create a short link for a long URL; redirect to the long URL; optional custom alias; optional expiry; click analytics.',
        '**Non-functional**: very fast redirects (p99 under ~50 ms), highly available, short codes that are hard to guess for private links.',
        '**Out of scope** (say it explicitly): user accounts and dashboards, unless the interviewer wants them.'
      ] },

      { type: 'h2', text: '2. Estimate the scale' },
      { type: 'code', language: 'text', code: `New links:   100 million / month  ≈ 40 writes per second
Redirects:   10 billion / month   ≈ 4,000 reads per second (peaks ~10x)
Read:write   ≈ 100 : 1            → this is a read-heavy system; cache aggressively
Storage:     ~500 bytes per link × 100M × 12 months × 5 years ≈ 3 TB` },
      { type: 'p', text: 'The ratio is the key insight: redirects dominate, so the design should make reads cheap above all.' },

      { type: 'h2', text: '3. Define the API' },
      { type: 'code', language: 'http', code: `POST /api/links          { "url": "https://...", "alias": "sale", "expiresAt": "2027-01-01" }
                         → 201 { "code": "aZ3k9Qx", "shortUrl": "https://sho.rt/aZ3k9Qx" }

GET  /{code}             → 302 Location: https://...   (404 if unknown, 410 if expired)

GET  /api/links/{code}/stats` },

      { type: 'h2', text: '4. Generate short codes' },
      { type: 'p', text: 'This is the heart of the problem. Seven characters of base62 (a–z, A–Z, 0–9) give 62⁷ ≈ 3.5 trillion codes, enough for decades at 100 million a month.' },
      { type: 'table', headers: ['Approach', 'Pros', 'Cons'], rows: [
        ['Hash the URL (MD5/SHA) and take 7 chars', 'Same URL gives the same code', 'Collisions must be detected and resolved'],
        ['Random 7-char code', 'Unpredictable', 'Needs a uniqueness check on insert'],
        ['Global counter encoded in base62', 'No collisions, short', 'Counter is a bottleneck; codes are guessable'],
        ['Counter ranges per server', 'No coordination per request, no collisions', 'Small gaps when a server restarts (harmless)']
      ] },
      { type: 'code', language: 'javascript', code: `const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

function toBase62(n) {
  let s = "";
  do {
    s = ALPHABET[n % 62] + s;
    n = Math.floor(n / 62);
  } while (n > 0);
  return s.padStart(7, "0");
}

console.log(toBase62(125));
console.log(toBase62(3_521_614_606_207));`, output: `0000021
ZZZZZZZ` },
      { type: 'p', text: 'A good answer for scale: each application server reserves a range of IDs (say one million at a time) from a coordination service or a database row, then hands them out locally. If guessable codes are a concern, scramble the number with a reversible permutation before encoding.' },

      { type: 'h2', text: '5. Choose storage' },
      { type: 'p', text: 'The data is a simple key-value mapping (`code → url, createdAt, expiresAt, ownerId`) with very high read volume and no joins. A key-value or wide-column store partitioned by code fits naturally; a relational database with sharding works too at this size. Whichever you pick, the lookup must be by primary key.' },

      { type: 'h2', text: '6. The high-level design' },
      { type: 'code', language: 'text', code: `Client ─► CDN / edge ─► Load balancer ─► Redirect service ─► Cache (Redis, LRU)
                                                │ cache miss
                                                ▼
                                        Key-value database (partitioned by code)
                                                │
              Redirect service ─► Queue ─► Analytics consumers ─► Analytics store

Create path: API service ─► ID range allocator ─► Database (+ write to cache)` },
      { type: 'ul', items: [
        '**Cache**: a small fraction of links receive most of the clicks, so an LRU cache in front of the database absorbs most reads.',
        '**Analytics**: never write click data synchronously in the redirect path. Publish an event to a queue and process it in the background.',
        '**CDN / edge**: very popular links can even be served from the edge.'
      ] },

      { type: 'h2', text: '7. Discuss trade-offs' },
      { type: 'table', headers: ['Decision', 'Option A', 'Option B'], rows: [
        ['Redirect status', '301: browsers cache it, less load, but repeat clicks are invisible to analytics', '302: every click hits you, accurate analytics'],
        ['Same long URL twice', 'Always create a new code (per-campaign analytics)', 'Return the existing code (needs a url → code index)'],
        ['Expiry cleanup', 'Check on read, delete lazily', 'Database TTL feature deletes automatically']
      ] },

      { type: 'h2', text: '8. Handle abuse and failures' },
      { type: 'ul', items: [
        'Rate-limit link creation per user and per IP.',
        'Scan destination URLs against malware and phishing lists; support takedowns.',
        'Replicate the database across availability zones; the cache can be rebuilt.',
        'If the ID allocator is down, servers keep using their already reserved ranges.'
      ] },

      { type: 'h2', text: 'Deep dive: the data model' },
      { type: 'code', language: 'sql', code: `CREATE TABLE links (
  code        VARCHAR(10) PRIMARY KEY,      -- lookups are always by code
  long_url    TEXT NOT NULL,
  owner_id    BIGINT,
  created_at  TIMESTAMPTZ NOT NULL DEFAULT now(),
  expires_at  TIMESTAMPTZ
);

-- Only if "same URL returns the same code" is required:
CREATE INDEX idx_links_owner_url ON links (owner_id, md5(long_url));` },
      { type: 'p', text: 'Keep click analytics out of this table. Writing a counter on every redirect would turn a read-heavy table into a write-heavy one and create hot rows for popular links.' },

      { type: 'h2', text: 'Deep dive: caching the hot links' },
      { type: 'ul', items: [
        'Cache-aside: on a redirect, check Redis; on a miss, read the database and store the result with a TTL.',
        'Popular links are read thousands of times per second, so a hit rate above 90% is realistic and keeps database load small.',
        'Cache negative results (“code does not exist”) briefly, so bots guessing random codes do not hit the database every time.',
        'When a hot key expires, let only one request refresh it (a short lock), so the database is not hit by a stampede.'
      ] },

      { type: 'h2', text: 'Going multi-region' },
      { type: 'p', text: 'If users are global, redirects should be served close to them. Links are written rarely and read constantly, so replicate the link data to every region and serve redirects locally, while creating links in one primary region (or partitioning ID ranges by region so each region can create codes independently).' },

      { type: 'h2', text: 'Follow-ups interviewers often ask' },
      { type: 'qa', question: 'How would you support custom aliases?', answer: ['Check that the alias is not reserved or taken, then insert it with the same primary key constraint. Keep aliases and generated codes in the same namespace, and reserve words like “api” and “admin”.'] },
      { type: 'qa', question: 'How do you prevent people from scanning all links?', answer: ['Use non-sequential codes (random or scrambled IDs), rate-limit redirects per IP for unknown codes, and offer longer codes or authentication for private links.'] },
      { type: 'qa', question: 'How would you count clicks accurately at very high volume?', answer: ['Publish click events to a queue, aggregate them in a stream processor into per-minute counts, and write the aggregates to an analytics store. Deduplicate bots using user agents and IP patterns.'] },

      { type: 'summary', items: [
        'Clarify scope, then estimate: the 100:1 read ratio drives the design.',
        'Short codes from counter ranges in base62 avoid collisions without per-request coordination.',
        'Key-value storage by code, an LRU cache in front, analytics through a queue.',
        'Discuss 301 vs 302, duplicates, expiry and abuse to show depth.'
      ] }
    ]
  },
  {
    slug: 'sliding-window-pattern-explained',
    title: 'The Sliding Window Pattern, Explained Properly',
    excerpt: 'How to recognise sliding window problems, the template that solves most of them, and worked examples from easy to hard.',
    category: 'DSA',
    tags: ['DSA', 'Sliding Window', 'Arrays'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'Sliding window is one of the highest-value patterns in coding interviews. It turns many O(n²) brute-force solutions into O(n) ones, and once you recognise it, a whole family of “longest/shortest subarray or substring” problems becomes routine.' },

      { type: 'h2', text: 'When to reach for it' },
      { type: 'ul', items: [
        'The problem is about a **contiguous** subarray or substring.',
        'It asks for the longest, shortest, maximum or number of windows that satisfy a condition.',
        'Extending the window and shrinking it from the left change the condition in a predictable direction.'
      ] },
      { type: 'callout', variant: 'warning', title: 'When it does not work', text: 'If adding an element can make a sum go down (negative numbers), you cannot decide which side to move. Use prefix sums with a hash map instead.' },

      { type: 'h2', text: 'Type 1: Fixed-size window' },
      { type: 'p', text: 'Maximum sum of any k consecutive elements. Instead of re-adding k numbers for every position, add the new element and subtract the one that left.' },
      { type: 'code', language: 'javascript', code: `function maxSumOfK(nums, k) {
  let sum = 0;
  for (let i = 0; i < k; i++) sum += nums[i];
  let best = sum;
  for (let right = k; right < nums.length; right++) {
    sum += nums[right] - nums[right - k];   // slide by one
    best = Math.max(best, sum);
  }
  return best;
}

console.log(maxSumOfK([2, 1, 5, 1, 3, 2], 3));`, output: `9` },

      { type: 'h2', text: 'Type 2: Variable-size window' },
      { type: 'p', text: 'Most interview problems use a variable window. The template:' },
      { type: 'code', language: 'javascript', code: `let left = 0;
for (let right = 0; right < n; right++) {
  add(arr[right]);                 // grow the window
  while (windowIsInvalid()) {      // shrink until valid again
    remove(arr[left]);
    left++;
  }
  updateAnswer(right - left + 1);  // window [left, right] is valid here
}` },
      { type: 'p', text: 'Each element enters the window once and leaves at most once, so the total work is O(n) even with the inner loop.' },

      { type: 'h3', text: 'Example: longest substring without repeating characters' },
      { type: 'code', language: 'javascript', code: `function longestUnique(s) {
  const lastSeen = new Map();
  let left = 0, best = 0;
  for (let right = 0; right < s.length; right++) {
    const ch = s[right];
    if (lastSeen.has(ch) && lastSeen.get(ch) >= left) left = lastSeen.get(ch) + 1;
    lastSeen.set(ch, right);
    best = Math.max(best, right - left + 1);
  }
  return best;
}

console.log(longestUnique("abcabcbb"), longestUnique("pwwkew"), longestUnique("abba"));`, output: `3 3 2` },
      { type: 'p', text: 'The `>= left` check matters: in "abba", the final "a" was last seen outside the current window, and without the check `left` would jump backwards.' },

      { type: 'h3', text: 'Example: smallest subarray with sum at least target' },
      { type: 'code', language: 'javascript', code: `function minSubarrayLen(target, nums) {
  let left = 0, sum = 0, best = Infinity;
  for (let right = 0; right < nums.length; right++) {
    sum += nums[right];
    while (sum >= target) {               // valid: try to shrink
      best = Math.min(best, right - left + 1);
      sum -= nums[left++];
    }
  }
  return best === Infinity ? 0 : best;
}

console.log(minSubarrayLen(7, [2, 3, 1, 2, 4, 3]));`, output: `2` },
      { type: 'p', text: 'Notice the flip: for “longest” problems you shrink while the window is invalid; for “shortest” problems you shrink while it is still valid, recording the answer as you go.' },

      { type: 'h3', text: 'Example: longest substring with at most k distinct characters' },
      { type: 'code', language: 'javascript', code: `function longestKDistinct(s, k) {
  const counts = new Map();
  let left = 0, best = 0;
  for (let right = 0; right < s.length; right++) {
    counts.set(s[right], (counts.get(s[right]) ?? 0) + 1);
    while (counts.size > k) {
      const c = s[left++];
      counts.set(c, counts.get(c) - 1);
      if (counts.get(c) === 0) counts.delete(c);
    }
    best = Math.max(best, right - left + 1);
  }
  return best;
}

console.log(longestKDistinct("eceba", 2), longestKDistinct("aa", 1));`, output: `3 2` },

      { type: 'h2', text: 'Recognising it in an interview' },
      { type: 'table', headers: ['Problem says…', 'Window state to track'], rows: [
        ['…without repeating characters', 'Last index of each character'],
        ['…at most k distinct', 'Counts map; size of the map'],
        ['…contains all characters of t', 'Counts needed vs counts in window'],
        ['…sum at least / at most (non-negative numbers)', 'Running sum'],
        ['…replace at most k characters to make all same', 'Counts and the most frequent count']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Using `if` instead of `while` when shrinking, leaving the window invalid.',
        'Updating the answer before the window is valid again.',
        'Applying the pattern to sums with negative numbers.',
        'Off-by-one errors in the window length: it is `right - left + 1`.'
      ] },

      { type: 'h2', text: 'The hard one: minimum window substring' },
      { type: 'p', text: 'Find the smallest substring of `s` that contains every character of `t` (with duplicates). It combines everything above: counts, a “how many requirements are met” counter, and shrinking while the window is valid.' },
      { type: 'code', language: 'javascript', code: `function minWindow(s, t) {
  const need = new Map();
  for (const c of t) need.set(c, (need.get(c) ?? 0) + 1);
  let missing = need.size;                  // characters whose count is not yet met
  const have = new Map();
  let left = 0, best = [0, Infinity];

  for (let right = 0; right < s.length; right++) {
    const c = s[right];
    if (need.has(c)) {
      have.set(c, (have.get(c) ?? 0) + 1);
      if (have.get(c) === need.get(c)) missing--;
    }
    while (missing === 0) {                 // valid window: record and shrink
      if (right - left < best[1] - best[0]) best = [left, right];
      const d = s[left++];
      if (need.has(d)) {
        if (have.get(d) === need.get(d)) missing++;
        have.set(d, have.get(d) - 1);
      }
    }
  }
  return best[1] === Infinity ? "" : s.slice(best[0], best[1] + 1);
}

console.log(minWindow("ADOBECODEBANC", "ABC"), JSON.stringify(minWindow("a", "aa")));`, output: `BANC ""` },
      { type: 'p', text: 'Tracking `missing` (how many distinct characters still lack enough copies) avoids comparing two maps on every step, keeping the whole thing O(|s| + |t|).' },

      { type: 'h2', text: 'Practice list, easiest first' },
      { type: 'table', headers: ['Problem', 'Window type', 'State'], rows: [
        ['Maximum sum subarray of size k', 'Fixed', 'Running sum'],
        ['Maximum average subarray', 'Fixed', 'Running sum'],
        ['Longest substring without repeating characters', 'Variable (longest)', 'Last seen index'],
        ['Minimum size subarray sum', 'Variable (shortest)', 'Running sum'],
        ['Longest substring with at most k distinct characters', 'Variable (longest)', 'Counts map'],
        ['Fruit into baskets', 'Variable (longest)', 'Counts map (k = 2)'],
        ['Longest repeating character replacement', 'Variable (longest)', 'Counts + max frequency'],
        ['Permutation in string / find all anagrams', 'Fixed', 'Counts compared to target'],
        ['Minimum window substring', 'Variable (shortest)', 'Counts + missing counter'],
        ['Sliding window maximum', 'Fixed', 'Monotonic deque']
      ] },

      { type: 'h2', text: 'How to explain it in an interview' },
      { type: 'ol', items: [
        'State the brute force: “check every subarray, O(n²) or worse”.',
        'Name the insight: “the condition changes predictably as the window grows or shrinks, so I can reuse work”.',
        'Describe the state you keep and when you shrink.',
        'Walk through a small example, then state O(n) time and the space for your map.'
      ] },

      { type: 'summary', items: [
        'Use sliding window for contiguous subarray/substring problems with a monotonic condition.',
        'Fixed windows add one element and remove one; variable windows grow right and shrink left.',
        'Each element enters and leaves once, so the solution is O(n).',
        'For negatives, switch to prefix sums with a hash map.'
      ] }
    ]
  },
  {
    slug: 'javascript-event-loop-explained',
    title: 'The JavaScript Event Loop, Finally Explained',
    excerpt: 'Call stack, task queue, microtasks and rendering, with output puzzles you can reason through instead of memorising.',
    category: 'Engineering',
    tags: ['JavaScript', 'Event Loop', 'Async'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'JavaScript runs your code on a single thread, yet it handles timers, network requests and user clicks without freezing. The mechanism behind this is the event loop. Understanding it explains a surprising number of bugs and is one of the most common interview topics.' },

      { type: 'h2', text: 'The pieces' },
      { type: 'table', headers: ['Piece', 'What it holds'], rows: [
        ['Call stack', 'The functions currently running'],
        ['Web APIs / runtime', 'Timers, network, DOM events: work handled outside your JavaScript'],
        ['Task queue (macrotasks)', 'Callbacks from `setTimeout`, `setInterval`, I/O, UI events'],
        ['Microtask queue', 'Promise callbacks (`then/catch/finally`), `await` continuations, `queueMicrotask`'],
        ['Rendering', 'Style, layout and paint, roughly once per frame']
      ] },

      { type: 'h2', text: 'The loop in four steps' },
      { type: 'ol', items: [
        'Run the current script (or one task) until the call stack is empty.',
        'Run **all** microtasks, including ones added while running microtasks.',
        'If it is time, let the browser render (and run `requestAnimationFrame` callbacks before it).',
        'Take the next task from the task queue and repeat.'
      ] },
      { type: 'p', text: 'The key consequence: promise callbacks always run before the next timer, even a `setTimeout(fn, 0)`.' },

      { type: 'h2', text: 'Puzzle 1: the classic' },
      { type: 'code', language: 'javascript', code: `console.log("1");
setTimeout(() => console.log("2"), 0);
Promise.resolve().then(() => console.log("3"));
console.log("4");`, output: `1
4
3
2` },
      { type: 'p', text: 'Synchronous code first (1, 4). The stack is empty, so microtasks run (3). Then the next task: the timer (2).' },

      { type: 'h2', text: 'Puzzle 2: async functions' },
      { type: 'code', language: 'javascript', code: `async function run() {
  console.log("A");
  await null;
  console.log("B");
}

console.log("start");
run();
Promise.resolve().then(() => console.log("C"));
console.log("end");`, output: `start
A
end
B
C` },
      { type: 'p', text: 'An `async` function runs synchronously until its first `await` (so "A" prints immediately). The rest of the function is queued as a microtask when `await` is reached. That happens before the `.then` for "C" is queued, so "B" runs first.' },

      { type: 'h2', text: 'Puzzle 3: microtasks that queue microtasks' },
      { type: 'code', language: 'javascript', code: `setTimeout(() => console.log("timeout"), 0);

Promise.resolve()
  .then(() => {
    console.log("then 1");
    queueMicrotask(() => console.log("nested microtask"));
  })
  .then(() => console.log("then 2"));`, output: `then 1
nested microtask
then 2
timeout` },
      { type: 'p', text: 'The microtask queue is drained completely, including microtasks added during draining, before any timer runs. That is also why an endless chain of microtasks freezes the page just like an infinite loop.' },

      { type: 'h2', text: 'Why this matters in real code' },
      { type: 'ul', items: [
        '**Long synchronous work blocks everything**: no clicks, no rendering, no timers. Split big jobs into chunks or move them to a Web Worker.',
        '**`setTimeout(fn, 0)` is not immediate**: it waits for the stack to empty and all microtasks to finish.',
        '**State updates and rendering**: frameworks batch updates and render later, which is why a value may not be updated on the next line.',
        '**Async does not mean parallel**: `async/await` makes waiting non-blocking, but your JavaScript still runs on one thread.'
      ] },
      { type: 'code', language: 'javascript', code: `// Keep the page responsive while processing a large list
async function processInChunks(items, work, chunkSize = 500) {
  for (let i = 0; i < items.length; i += chunkSize) {
    items.slice(i, i + chunkSize).forEach(work);
    await new Promise((resolve) => setTimeout(resolve, 0)); // let the browser breathe
  }
}` },

      { type: 'h2', text: 'Node.js is slightly different' },
      { type: 'p', text: 'Node’s event loop has phases (timers, I/O polling, `setImmediate` checks, close callbacks). Two extras to know: `process.nextTick` callbacks run before promise microtasks, and inside an I/O callback `setImmediate` always runs before a `setTimeout(…, 0)`.' },

      { type: 'h2', text: 'How to answer output questions in interviews' },
      { type: 'ol', items: [
        'Draw three columns: call stack, microtasks, tasks.',
        'Walk through the code line by line, placing callbacks in the right column.',
        'Remember: Promise executors and code before the first `await` run synchronously.',
        'When the stack empties, drain all microtasks, then take one task, and repeat.'
      ] },

      { type: 'h2', text: 'Puzzle 4: timers and promises nested inside each other' },
      { type: 'code', language: 'javascript', code: `setTimeout(() => {
  console.log("t1");
  Promise.resolve().then(() => console.log("promise inside t1"));
}, 0);

setTimeout(() => console.log("t2"), 0);

Promise.resolve().then(() => {
  console.log("p1");
  setTimeout(() => console.log("t3"), 0);
});`, output: `p1
t1
promise inside t1
t2
t3` },
      { type: 'p', text: 'Step by step: the script queues t1 and t2 and one microtask. The microtask runs first (p1) and queues t3 behind t2. Then t1 runs, and its promise callback runs immediately after it, before t2, because microtasks are drained after every single task. Finally t2, then t3.' },

      { type: 'h2', text: 'Where rendering fits' },
      { type: 'p', text: 'The browser can only paint between tasks. If you change the DOM and then run heavy work in the same task, the user never sees the change until the work finishes.' },
      { type: 'compare', badLabel: 'Spinner never appears', bad: `button.onclick = () => {
  spinner.hidden = false;
  generateHugeReport();   // blocks
  spinner.hidden = true;
};`, goodLabel: 'Spinner shows first', good: `button.onclick = () => {
  spinner.hidden = false;
  requestAnimationFrame(() => {
    setTimeout(() => {    // after the paint
      generateHugeReport();
      spinner.hidden = true;
    }, 0);
  });
};`, text: 'Better still, move the heavy work to a Web Worker so the page stays interactive.' },
      { type: 'p', text: '`requestAnimationFrame` callbacks run just before the next paint, which makes them the right place for visual updates and animations; the nested `setTimeout` pushes the heavy work to after that paint.' },

      { type: 'h2', text: 'Bugs caused by misunderstanding the event loop' },
      { type: 'ul', items: [
        'Reading a value right after setting React state and getting the old value.',
        'A `forEach` with an async callback that does not wait, so code after it runs too early.',
        'Expecting `setTimeout(fn, 0)` to run before a promise callback.',
        'A busy loop or huge `JSON.parse` that makes clicks feel ignored (poor INP).',
        'Recursive microtasks that freeze the tab without any error.'
      ] },

      { type: 'summary', items: [
        'One thread runs your code; the runtime handles waiting.',
        'After each task, all microtasks run before the next task or render.',
        'Promise callbacks beat timers; async functions are synchronous until the first await.',
        'Long synchronous work and endless microtasks both freeze the page.'
      ] }
    ]
  },
  {
    slug: 'react-rerenders-explained',
    title: 'Why Your React App Re-renders (and How to Fix It)',
    excerpt: 'The four reasons components re-render, how to find the expensive ones, and fixes from moving state down to memoisation.',
    category: 'Engineering',
    tags: ['React', 'Performance', 'Hooks'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'A slow React app is rarely slow because React is slow. It is usually doing far more rendering work than it needs to. Before reaching for `useMemo` everywhere, it helps to understand exactly why components re-render.' },

      { type: 'h2', text: 'The four reasons a component re-renders' },
      { type: 'ol', items: [
        '**Its own state changed** (`useState`, `useReducer`).',
        '**Its parent re-rendered**: by default every child re-renders too, whether or not its props changed.',
        '**A context it reads changed** value.',
        '**A hook it uses updated** internal state (a data-fetching or store hook).'
      ] },
      { type: 'callout', variant: 'note', title: 'A common misconception', text: '“Components re-render when their props change” is only half true. Changing props is a result of the parent re-rendering, which re-renders the child anyway unless it is memoised.' },
      { type: 'p', text: 'Also remember: re-rendering means calling your component function and comparing the result. It does not necessarily mean touching the DOM. Many re-renders are cheap and harmless; focus on the ones that are slow or frequent.' },

      { type: 'h2', text: 'Step 1: Measure before fixing' },
      { type: 'ul', items: [
        'React DevTools → Profiler: record the slow interaction and look for components that render often or take long. Enable “Record why each component rendered”.',
        'Components tab → “Highlight updates when components render” shows render storms visually.',
        'Chrome’s Performance panel shows long tasks that hurt responsiveness.'
      ] },

      { type: 'h2', text: 'Fix 1: Move state down' },
      { type: 'p', text: 'The cheapest fix is often structural. If typing in a search box re-renders an expensive chart, the state is too high in the tree.' },
      { type: 'compare', badLabel: 'Whole page re-renders on each key', bad: `function Dashboard() {
  const [query, setQuery] = useState("");
  return (
    <>
      <input value={query}
        onChange={(e) => setQuery(e.target.value)} />
      <ExpensiveChart />
    </>
  );
}`, goodLabel: 'Only the search re-renders', good: `function SearchBox() {
  const [query, setQuery] = useState("");
  return <input value={query}
    onChange={(e) => setQuery(e.target.value)} />;
}

function Dashboard() {
  return (<><SearchBox /><ExpensiveChart /></>);
}`, text: 'State lives in the component that uses it, so the chart is no longer affected.' },

      { type: 'h2', text: 'Fix 2: Pass expensive parts as children' },
      { type: 'code', language: 'jsx', code: `function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setSidebarOpen((o) => !o)}>Menu</button>
      {sidebarOpen && <Sidebar />}
      {children}   {/* created by the parent: not re-rendered when sidebarOpen changes */}
    </div>
  );
}

<Layout><ExpensiveChart /></Layout>` },

      { type: 'h2', text: 'Fix 3: Memoise, correctly' },
      { type: 'p', text: '`React.memo` skips re-rendering a component when its props are shallow-equal. It only works if the props are actually stable, which is where `useMemo` and `useCallback` come in.' },
      { type: 'code', language: 'jsx', code: `const ProductList = React.memo(function ProductList({ items, onSelect }) {
  return items.map((p) => <Row key={p.id} product={p} onSelect={onSelect} />);
});

function Shop({ products, query }) {
  const visible = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(query)),
    [products, query]
  );
  const handleSelect = useCallback((id) => addToCart(id), []);
  return <ProductList items={visible} onSelect={handleSelect} />;
}` },
      { type: 'ul', items: [
        'Without `useMemo`, `visible` is a new array each render, so `React.memo` never skips.',
        '`useCallback` on a handler passed to a plain `<button>` does nothing useful.',
        'Memoising cheap calculations costs about as much as recomputing them.'
      ] },

      { type: 'h2', text: 'Fix 4: Be careful with context' },
      { type: 'p', text: 'Every component that reads a context re-renders when its value changes, and `React.memo` cannot stop it. Two common problems:' },
      { type: 'ul', items: [
        'Passing a new object as the value on every render: wrap it in `useMemo`.',
        'Putting fast-changing and rarely-changing data in one context: split it (for example, state and dispatch in separate contexts), or use a store with selectors for frequently updated global state.'
      ] },

      { type: 'h2', text: 'Fix 5: Large lists and heavy updates' },
      { type: 'ul', items: [
        '**Virtualise** long lists so only visible rows are in the DOM (react-window, TanStack Virtual).',
        '**Defer** expensive views with `useDeferredValue` or `useTransition`, so typing stays responsive.',
        '**Stable keys**: never use random values as keys; it remounts every row on every render.'
      ] },

      { type: 'h2', text: 'Mistakes that cause unnecessary renders' },
      { type: 'ul', items: [
        'Defining a component inside another component: it is a new type every render and remounts.',
        'Storing derived values in state and syncing them with effects (an extra render each time).',
        'Mutating state, then forcing updates to compensate.',
        'Wrapping everything in `useMemo` without a measured problem.'
      ] },

      { type: 'h2', text: 'Keys, identity and accidental remounts' },
      { type: 'p', text: 'Re-rendering keeps a component’s state; remounting throws it away. React decides between them using the component’s type, its position, and its `key`.' },
      { type: 'compare', badLabel: 'Remounts every render', bad: `function Page({ items }) {
  // New component type each render:
  // inputs lose focus and state resets
  const Row = ({ item }) => <input defaultValue={item.name} />;
  return items.map((item) => <Row key={Math.random()} item={item} />);
}`, goodLabel: 'Stable identity', good: `const Row = ({ item }) => <input defaultValue={item.name} />;

function Page({ items }) {
  return items.map((item) => <Row key={item.id} item={item} />);
}`, text: 'Define components at the top level and use stable ids as keys. Random keys and inline component definitions are two of the most common causes of “my input loses focus” bugs.' },
      { type: 'p', text: 'You can also use `key` deliberately: `<ProfileForm key={userId} />` resets the form’s state whenever the user changes, which is cleaner than syncing state with an effect.' },

      { type: 'h2', text: 'Worked example: a slow search page' },
      { type: 'ol', items: [
        '**Symptom**: typing in the search box lags on mid-range phones.',
        '**Profile**: every keystroke re-renders the whole page, including a 2,000-row product table that takes around 80 ms.',
        '**Fix 1**: move the search input’s state into a `SearchBox` component. The rest of the page stops re-rendering while you type.',
        '**Fix 2**: the table still re-renders when results change, so filter with `useDeferredValue(query)` and wrap the table in `React.memo`.',
        '**Fix 3**: virtualise the table so only visible rows are rendered.',
        '**Verify**: profile again. Typing is now instant and the table updates a moment later without blocking input.'
      ] },

      { type: 'h2', text: 'When to use useMemo and useCallback' },
      { type: 'table', headers: ['Situation', 'Use it?'], rows: [
        ['Expensive calculation (sorting or filtering thousands of items)', 'Yes, `useMemo`'],
        ['Object, array or function passed to a `React.memo` child', 'Yes'],
        ['Value used in an effect’s dependency array that would otherwise change every render', 'Yes'],
        ['Handler passed to a plain DOM element like `<button>`', 'No'],
        ['Cheap calculation like string formatting', 'No']
      ] },
      { type: 'callout', variant: 'note', title: 'The React Compiler', text: 'Recent React tooling can memoise components and values automatically at build time, removing much of the manual `useMemo` and `useCallback`. Understanding why memoisation works is still what lets you debug performance when things go wrong.' },

      { type: 'summary', items: [
        'Components re-render on own state, parent render, context change or hook update.',
        'Measure with the React Profiler before optimising.',
        'Move state down and pass expensive parts as children first.',
        'Memoisation works only with stable props; context needs care.',
        'Virtualise long lists and defer expensive views.'
      ] }
    ]
  },
  {
    slug: 'database-indexing-explained',
    title: 'Database Indexes Explained for Developers',
    excerpt: 'How indexes work under the hood, how to design compound indexes, how to read a query plan, and when indexes hurt.',
    category: 'Engineering',
    tags: ['Databases', 'SQL', 'MongoDB', 'Performance'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'When an endpoint gets slower as data grows, a missing or badly designed index is the most likely cause. Indexes are one of the highest-leverage things a backend developer can understand, and they work the same way in Postgres, MySQL and MongoDB.' },

      { type: 'h2', text: 'What an index is' },
      { type: 'p', text: 'Without an index, the database must read every row to find matches: a full table scan, O(n). An index is a separate, sorted data structure (usually a B-tree) of column values pointing to rows. Finding a value becomes a tree search, roughly O(log n), and ranges of values are next to each other.' },
      { type: 'p', text: 'Think of the index at the back of a book: instead of reading every page for “caching”, you look it up alphabetically and jump to the right pages.' },

      { type: 'h2', text: 'See the difference' },
      { type: 'code', language: 'sql', code: `EXPLAIN ANALYZE
SELECT * FROM orders WHERE customer_id = 4821;

-- Before:  Seq Scan on orders  (rows=5,000,000)   Execution Time: 812 ms
CREATE INDEX idx_orders_customer ON orders (customer_id);
-- After:   Index Scan using idx_orders_customer   Execution Time: 0.4 ms` },
      { type: 'p', text: 'The numbers vary by data and hardware, but going from a full scan to an index lookup is commonly a difference of several orders of magnitude.' },

      { type: 'h2', text: 'Compound indexes and column order' },
      { type: 'p', text: 'A compound index sorts by the first column, then by the second within each value of the first, and so on. It can serve queries that use a **prefix** of its columns.' },
      { type: 'table', headers: ['Index on (customer_id, status, created_at)', 'Can use the index?'], rows: [
        ['`WHERE customer_id = ?`', 'Yes'],
        ['`WHERE customer_id = ? AND status = ?`', 'Yes'],
        ['`WHERE customer_id = ? ORDER BY ...` with status skipped', 'Partly (filter by customer, then sort)'],
        ['`WHERE status = ?`', 'No: status is not a prefix'],
        ['`WHERE created_at > ?`', 'No']
      ] },
      { type: 'h3', text: 'The ESR rule' },
      { type: 'p', text: 'A reliable way to order columns: **E**quality filters first, then **S**ort columns, then **R**ange filters.' },
      { type: 'code', language: 'sql', code: `-- Query: a customer's paid orders over 1000, newest first
SELECT * FROM orders
WHERE customer_id = 4821 AND status = 'paid' AND total > 1000
ORDER BY created_at DESC
LIMIT 20;

-- Equality (customer_id, status) → Sort (created_at) → Range (total)
CREATE INDEX idx_orders_esr ON orders (customer_id, status, created_at DESC, total);` },
      { type: 'p', text: 'With this order, the matching rows are already sorted by `created_at` inside the index, so the database can read the first 20 and stop, with no separate sort step.' },

      { type: 'h2', text: 'Covering indexes' },
      { type: 'p', text: 'If every column a query needs is in the index, the database can answer from the index alone without visiting the table (an index-only scan in Postgres, a covered query in MongoDB). Postgres lets you add extra columns with `INCLUDE`:' },
      { type: 'code', language: 'sql', code: `CREATE INDEX idx_orders_customer_cover
  ON orders (customer_id, created_at DESC) INCLUDE (total, status);` },

      { type: 'h2', text: 'The same ideas in MongoDB' },
      { type: 'code', language: 'javascript', code: `db.orders.createIndex({ customerId: 1, status: 1, createdAt: -1, total: 1 });

db.orders.find({ customerId: 4821, status: "paid", total: { $gt: 1000 } })
  .sort({ createdAt: -1 })
  .limit(20)
  .explain("executionStats");
// Look for IXSCAN (not COLLSCAN), no SORT stage, and
// totalDocsExamined close to nReturned` },

      { type: 'h2', text: 'Reading a query plan' },
      { type: 'table', headers: ['You see', 'It means'], rows: [
        ['Seq Scan / COLLSCAN on a large table', 'No usable index'],
        ['Rows examined ≫ rows returned', 'Index not selective enough, or wrong column order'],
        ['A separate Sort step', 'Index order does not match ORDER BY'],
        ['Index Only Scan / covered query', 'Best case: answered from the index']
      ] },

      { type: 'h2', text: 'Why an index might be ignored' },
      { type: 'ul', items: [
        'A function on the column: `WHERE LOWER(email) = ?` needs an index on `LOWER(email)`.',
        'Leading wildcards: `LIKE \'%phone\'` cannot use a normal B-tree index.',
        'Type mismatches between the column and the parameter.',
        'Low selectivity: if a condition matches most rows, a scan can genuinely be faster.',
        'Outdated statistics: run `ANALYZE` so the planner knows your data.'
      ] },

      { type: 'h2', text: 'When indexes hurt' },
      { type: 'ul', items: [
        'Every insert, update and delete must also update every index, so write-heavy tables pay for each extra index.',
        'Indexes use disk and memory; they work best when the hot parts fit in RAM.',
        'Overlapping indexes waste space: `(a)` is redundant if you already have `(a, b)`.',
        'Find unused indexes (`pg_stat_user_indexes` in Postgres, `$indexStats` in MongoDB) and remove them.'
      ] },

      { type: 'h2', text: 'A practical checklist' },
      { type: 'ol', items: [
        'Find slow queries (slow query log, APM, `pg_stat_statements`).',
        'Run `EXPLAIN ANALYZE` / `explain("executionStats")`.',
        'Design a compound index with the ESR rule for the most important queries.',
        'Verify the plan changed and the timing improved.',
        'Review unused and duplicate indexes regularly.'
      ] },

      { type: 'h2', text: 'Index types worth knowing' },
      { type: 'table', headers: ['Type', 'Good for', 'Example'], rows: [
        ['B-tree (default)', 'Equality, ranges, sorting', 'Most indexes'],
        ['Unique', 'Enforcing uniqueness', 'One account per email'],
        ['Partial / filtered', 'Indexing only rows you query', 'Open tickets, active users'],
        ['Expression', 'Queries on computed values', '`LOWER(email)`'],
        ['GIN (Postgres)', 'Arrays, JSONB, full-text', 'Tags, document search'],
        ['Text / search', 'Word search', 'Product search'],
        ['TTL (MongoDB)', 'Auto-deleting old documents', 'Sessions, OTP codes']
      ] },
      { type: 'code', language: 'sql', code: `-- Case-insensitive email lookups
CREATE UNIQUE INDEX idx_users_email_lower ON users (LOWER(email));

-- Only open tickets are queried by assignee: a much smaller index
CREATE INDEX idx_tickets_open ON tickets (assignee_id, priority DESC)
  WHERE status = 'open';` },

      { type: 'h2', text: 'Indexes and pagination' },
      { type: 'p', text: '`OFFSET` pagination gets slower the deeper you go, because the database still walks past every skipped row. Keyset (cursor) pagination uses the index to jump straight to the next page:' },
      { type: 'code', language: 'sql', code: `-- Index: (created_at DESC, id DESC)
SELECT id, title, created_at
FROM posts
WHERE (created_at, id) < ($1, $2)      -- values from the last row of the previous page
ORDER BY created_at DESC, id DESC
LIMIT 20;` },
      { type: 'p', text: 'Every page costs the same, and rows inserted while the user is paging do not cause duplicates or skipped items.' },

      { type: 'h2', text: 'A typical debugging story' },
      { type: 'ol', items: [
        'An “orders” page that was fast at launch now takes several seconds for some customers.',
        'The slow query log shows `WHERE customer_id = ? AND status = ? ORDER BY created_at DESC LIMIT 20`.',
        '`EXPLAIN ANALYZE` shows an index on `customer_id` alone, then a filter on status, then a sort of thousands of rows.',
        'A compound index on `(customer_id, status, created_at DESC)` removes the filter and the sort.',
        'The plan now reads exactly 20 index entries; the page is fast again, and the old single-column index can be dropped because the new one covers it as a prefix.'
      ] },

      { type: 'h2', text: 'Frequently asked questions' },
      { type: 'qa', question: 'Should I index every column I filter on?', answer: ['No. Index the queries that matter, usually with compound indexes designed for them. Many single-column indexes waste space, slow writes, and often are not used together efficiently.'] },
      { type: 'qa', question: 'Do foreign keys get indexes automatically?', answer: ['Not in Postgres: the referenced primary key is indexed, but the foreign key column in the child table is not. Add an index if you join or filter on it, which you almost always do. MySQL’s InnoDB does create one automatically.'] },
      { type: 'qa', question: 'How do I know an index is unused?', answer: ['Postgres tracks usage in `pg_stat_user_indexes` (look for `idx_scan = 0` over a long period); MongoDB has `$indexStats`. Confirm over a full business cycle before dropping anything.'] },

      { type: 'summary', items: [
        'An index is a sorted structure that turns full scans into fast lookups.',
        'Compound indexes serve prefixes of their columns; order them Equality, Sort, Range.',
        'Covering indexes answer queries without touching the table.',
        'Always confirm with the query plan, and remember indexes slow writes.'
      ] }
    ]
  }
];
